/**
 * SymbolDataPanel — everything known about the charted symbol, in tabs.
 *
 * The same views the PIP shows in a corner, at a size you can read. It is the
 * second half of one feature rather than a feature of its own: `symbol-views.tsx`
 * is the registry, and this file is a frame around it.
 *
 * ## One panel for the layout, re-pointed rather than duplicated
 *
 * One key in the vertical strip, aimed at the active chart, and only ever
 * **one** panel. It was a `⊞` on every chart's toolbar — one key per chart in
 * front of a single panel, where pressing another chart's key moved this one
 * rather than opening a second. A second panel would mean two things claiming to
 * be *the* data panel, and the user deciding which one they are looking at.
 *
 * ## It persists nothing
 *
 * Open, which tab, which chart it points at — all view state (D8), held in `App`
 * beside `plansOpen`. Only the PIP is workspace data, because only the PIP is
 * part of what a chart *is*.
 *
 * ## The tab strip does not change shape over time — only across markets
 *
 * A view with no source yet keeps its tab, marked with a dot and rendering an
 * empty state that names what is missing. A tab that appears the day an endpoint
 * lands moves every tab beside it, and a reader who learned where "Peers" was
 * has to learn again.
 *
 * **The one exception is the symbol's market**, and it is the opposite case:
 * `symbolViewsFor` drops News and Sentiment for a non-US symbol because there is
 * no feed coming for them. A dotted tab is a promise; those two would be a dead
 * end. Charting a US symbol brings them straight back, and the stored tab is
 * left alone so it comes back selected.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { DraggableDialog } from "./DraggableDialog";
import { PhoneCard, isEtfSymbol, symbolViewsFor, type SymbolView } from "./symbol-views";
import { DataCards } from "./DataCards";
import { DETACH_REFUSED_LABEL, shownView } from "../lib/detach";
import { useViewDetach } from "./view-detach";
import { QueVeeMark } from "./QueVeeMark";
import { symbolLabel, useSymbolIndex, useSymbolName } from "../lib/symbol-index";

export interface SymbolDataPanelProps {
  symbol: string;
  tab: SymbolView["id"];
  onTab: (tab: SymbolView["id"]) => void;
  onClose: () => void;
  /**
   * Point the chart this panel belongs to at another symbol.
   *
   * The panel is opened *for* a chart group, so following a peer re-points that
   * group — the panel and the chart stay describing the same company, which is
   * the only arrangement where a reader can trust either.
   */
  onSymbol?: (symbol: string) => void;
  /**
   * Send the panel to its own window. Absent where detaching is not offered —
   * a phone, or an installed PWA where a popup can come up detached and lose
   * its callback.
   */
  onDetach?: (() => void) | undefined;
  /** The chart group this panel was opened for. See `SymbolViewProps`. */
  chartGroupId?: string;
  /**
   * This browser has already refused to open a window.
   *
   * Distinct from `onDetach` being absent: that means detaching is not offered
   * here at all, where this means it was offered, tried, and cannot work. The
   * control stays visible and goes disabled, rather than vanishing between one
   * click and the next with nothing said.
   */
  detachRefused?: boolean | undefined;
}

export interface HoveredDataPanelProps {
  symbol: string;
  tab: SymbolView["id"];
  onTab: (tab: SymbolView["id"]) => void;
  /** The key's rectangle, in viewport coordinates. */
  anchor: DOMRect;
  onEnter: () => void;
  onLeave: () => void;
  /**
   * Which side of the key to open on, for a key in the **vertical strip**.
   *
   * The strip is a 34px column standing between the charts and the screener, so
   * a panel dropped below its key covers the rest of the strip and reaches the
   * bottom of the viewport on any key below the first. Beside it is the only
   * direction with room. Omitted ⇒ the original below-the-key placement, which
   * is right for a key in a horizontal row.
   *
   * `left`/`right` name where the *panel* goes, and the caller passes the chart
   * side — the same rule the saved-filter popover follows, and for the same
   * reason: the strip's other neighbour is the surface this panel replaces.
   */
  side?: "left" | "right" | undefined;
}

/**
 * The hover form: beside or under the key that opened it, gone when the pointer
 * goes.
 *
 * **Not the draggable dialog**, which centres itself and carries a close button
 * — both wrong for something the pointer is holding open. A peek belongs where
 * you are looking, and a control you have to travel to in order to dismiss is a
 * control a hover panel does not need.
 *
 * It hears the pointer, and that is not decoration: entering it cancels the
 * pending close, which is the only thing that lets you reach it at all. See
 * `lib/use-hover-intent.ts`.
 */
export function HoveredDataPanel({
  symbol,
  tab,
  onTab,
  anchor,
  onEnter,
  onLeave,
  side,
}: HoveredDataPanelProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [at, setAt] = useState<{ left: number; top: number } | null>(null);

  // `fixed`, measured after render, exactly as every popover in this app is:
  // each one sits inside a grid cell with `overflow: hidden`, so an absolutely
  // positioned panel is clipped by an ancestor it never sees.
  useLayoutEffect(() => {
    const box = ref.current;
    if (!box) return;
    const { offsetWidth: w, offsetHeight: h } = box;
    /** Kept fully on screen on the axis it is not being placed along. */
    const clamp = (v: number, size: number, limit: number) =>
      Math.max(4, Math.min(v, limit - size - 4));

    if (side) {
      // Beside the key, and **top-aligned to it rather than centred on it**: the
      // key's own row is where the reader is looking, and a panel centred on a
      // 34px key hangs half its height above that line — which on the strip's
      // top key means hanging off the top of the viewport. Clamping then only
      // has to deal with the bottom edge.
      setAt({
        left:
          side === "left"
            ? clamp(anchor.left - w - 6, w, window.innerWidth)
            : clamp(anchor.right + 6, w, window.innerWidth),
        top: clamp(anchor.top, h, window.innerHeight),
      });
      return;
    }

    const below = anchor.bottom + 6;
    // Flipped above the key only when there is genuinely no room below *and*
    // there is room above — otherwise a short viewport bounces the panel over
    // the toolbar it came from.
    const flip = below + h > window.innerHeight && anchor.top - h - 6 > 0;
    setAt({
      left: Math.max(4, Math.min(anchor.left, window.innerWidth - w - 4)),
      top: flip ? anchor.top - h - 6 : below,
    });
  }, [anchor, symbol, side]);

  return (
    <div
      ref={ref}
      onPointerEnter={onEnter}
      onPointerLeave={onLeave}
      style={{
        ...HOVER_HOST,
        left: at?.left ?? 0,
        top: at?.top ?? 0,
        // Hidden rather than absent until measured: it has to be in the document
        // to have a size, and one frame in the corner of the screen is a flicker
        // every reader notices.
        visibility: at ? "visible" : "hidden",
      }}
    >
      <SymbolDataTabs symbol={symbol} tab={tab} onTab={onTab} width={HOVER_WIDTH} />
    </div>
  );
}

/**
 * The panel docked over the chart, in the app's own draggable dialog.
 *
 * The frame and the content are split because the detached form has neither: a
 * window *is* the frame, and a 440px card floating inside a 460px one reads as a
 * mistake. {@link SymbolDataTabs} is what both render.
 */
export function SymbolDataPanel({
  symbol,
  tab,
  onTab,
  onClose,
  onDetach,
  detachRefused,
  onSymbol,
  chartGroupId,
}: SymbolDataPanelProps) {
  const name = useSymbolName(symbol);
  // Said once, and used as the tooltip, the accessible name and the disabled
  // state, so the three cannot disagree about whether the control works.
  const label = detachRefused ? DETACH_REFUSED_LABEL : "Open in its own window";
  return (
    <DraggableDialog
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {symbolLabel(symbol, name)}
          {onDetach && (
            <button
              type="button"
              onClick={onDetach}
              disabled={detachRefused}
              title={label}
              aria-label={label}
              style={detachRefused ? DETACH_REFUSED : DETACH}
            >
              ⧉
            </button>
          )}
        </span>
      }
      onClose={onClose}
    >
      <SymbolDataTabs
        symbol={symbol}
        tab={tab}
        onTab={onTab}
        width={DOCKED_WIDTH}
        onSymbol={onSymbol}
        chartGroupId={chartGroupId}
      />
    </DraggableDialog>
  );
}

/**
 * The panel filling a **grid slot** — the screener's, in place of it.
 *
 * The fourth host, and the one a click now opens. It is not a dialog: it has no
 * drag, no shadow and no float, because it is not over anything — it *is* the
 * panel in that cell, sized by the track the screener was sized by. All six
 * views are in reach at whatever width the user gave the screener, which is the
 * whole reason for taking the slot rather than covering a chart with a 560px
 * card.
 *
 * The dialog above stays for the layout that has no screener to replace: there
 * is no slot to take, and floating over a chart is better than nowhere.
 *
 * The header is the dialog's, minus the drag: the symbol, the detach control,
 * and a ✕ that puts the screener back. The ✕ is what makes this a place you can
 * leave without knowing that the strip's screener key would also do it.
 */
export function DockedDataPanel({
  symbol,
  tab,
  onTab,
  onClose,
  onDetach,
  detachRefused,
  onSymbol,
  chartGroupId,
}: SymbolDataPanelProps) {
  const name = useSymbolName(symbol);
  const label = detachRefused ? DETACH_REFUSED_LABEL : "Open in its own window";
  return (
    <div style={SLOT_HOST}>
      <div style={SLOT_HEAD}>
        <span style={SLOT_TITLE}>{symbolLabel(symbol, name)}</span>
        {onDetach && (
          <button
            type="button"
            onClick={onDetach}
            disabled={detachRefused}
            title={label}
            aria-label={label}
            style={detachRefused ? DETACH_REFUSED : DETACH}
          >
            ⧉
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          title="Close the data panel and show the screener"
          aria-label="Close the data panel and show the screener"
          style={SLOT_CLOSE}
        >
          ✕
        </button>
      </div>
      {/* No `width`, which is what puts `SymbolDataTabs` on the slot's own
          height rather than the docked dialog's fixed one — see its `height`. */}
      <SymbolDataTabs
        symbol={symbol}
        tab={tab}
        onTab={onTab}
        onSymbol={onSymbol}
        chartGroupId={chartGroupId}
      />
    </div>
  );
}

/**
 * How wide the two hosts are, and the number that decides it.
 *
 * **Counted, not guessed.** Fundamentals renders six columns of years plus a
 * label column: the label needs ~108px and a cell holding `466.82B` with a
 * year-on-year arrow needs ~65px at 11px tabular figures, so the table wants
 * about **500px** before the panel's own padding. At the old 440 it scrolled
 * sideways on every symbol, which is exactly the failure the table's own
 * `overflow-x` was meant to be a *fallback* for rather than the normal case.
 *
 * The hover form stays narrower because it is a preview over the chart rather
 * than something to read — it keeps the scroll, and that is the right trade at
 * a size where widening would cover the bars underneath.
 */
const DOCKED_WIDTH = 560;
const HOVER_WIDTH = 420;

/**
 * And how tall, which is a fixed number for a reason that is not obvious.
 *
 * **The dialog is centred until it is dragged** — `top: 50%` with a
 * `translate(-50%, -50%)` — so its *centre* is pinned and its top edge is
 * whatever half its height happens to be. A tab whose content is shorter than
 * the last one therefore makes the whole panel slide **down**, taking the tab
 * strip with it: switching from Profile to Peers moved the row of tabs out from
 * under the pointer that had just clicked one.
 *
 * A fixed height cures it at the source. The panel is the same size on every
 * tab, so changing tab moves nothing, and each tab scrolls its own content —
 * which is what `BODY` was already set up to do.
 *
 * `min()` rather than a bare pixel count: 760 is comfortable on a monitor and
 * taller than a laptop viewport, and the dialog's own 90vh cap would otherwise
 * be doing the clamping — which puts the height back under the content's
 * control on exactly the screens where it matters.
 */
const DOCKED_HEIGHT = "min(760px, 78vh)";

/**
 * The tab strip and the view under it — everything except the frame.
 *
 * Rendered docked at a fixed width and detached at the window's, which is the
 * only difference between the two hosts.
 */
export function SymbolDataTabs({
  symbol,
  tab,
  onTab,
  width,
  onSymbol,
  chartGroupId,
  cards,
}: {
  symbol: string;
  tab: SymbolView["id"];
  onTab: (tab: SymbolView["id"]) => void;
  width?: number;
  /**
   * Point the chart this panel belongs to at another symbol.
   *
   * Optional, because the hover preview has no business re-pointing anything —
   * it appears under the cursor and vanishes, and a chart that changed because
   * a pointer passed over a row would be a chart nobody trusts.
   */
  onSymbol?: (symbol: string) => void;
  /** The chart group this panel describes, for the one view that reads it. */
  chartGroupId?: string;
  /**
   * The phone's form: each view a card, swiped sideways (`DataCards`; mobile.md,
   * "On a phone the views are cards you swipe"). `phone` replaces a view's
   * renderer with one built for the width — Holdings and Fin St.
   */
  cards?: { phone?: Partial<Record<SymbolView["id"], SymbolView["Render"]>> };
}) {
  // **Controlled, and held by `App`.** It was local state until the action
  // registry needed it: "open the fundamentals" is one call that opens the panel
  // *and* names a tab, and a tab nothing outside the component can set is a tab
  // the agent and the command search cannot reach.
  //
  // Lifting it also keeps the behaviour that made it worth having — the tab does
  // not reset when the symbol changes, which is the whole reason to compare two
  // symbols in one panel. `App` holds one value across a re-point.
  // **The trail, and why it holds the whole path rather than a stack of detours.**
  //
  // Clicking a peer re-points the chart as well as the panel, so `symbol` — the
  // chart group's own symbol — changes underneath us. The trail therefore
  // cannot use it as the way home: by the time Back is pressed, the prop *is*
  // the peer. So the first step records where the wandering started and every
  // step after appends, which makes `trail[0]` the root no matter how far in
  // the reader has gone.
  //
  // Empty means at rest. One entry means home. Two or more means there is
  // somewhere to go back to, which is exactly when the strip appears.
  const [trail, setTrail] = useState<string[]>([]);
  const shown = trail.length > 0 ? trail[trail.length - 1]! : symbol;

  // Resolved against the views this *symbol* has rather than against all six:
  // news and sentiment have no India source, so a reader sitting on News who
  // charts `RELIANCE.NS` would otherwise hold a tab that is no longer in the
  // strip. Falling back to the first view is what puts them somewhere real, and
  // `tab` itself is deliberately left alone — charting a US symbol restores it.
  // Subscribed so the tabs re-render when the index lands: only the index says
  // whether `shown` is an ETF, which decides Components vs ETFs.
  useSymbolIndex(true);
  const views = symbolViewsFor(shown);
  const etf = isEtfSymbol(shown);
  // **An ETF opens on Components** (docs/plans/etf-components.md) — whether it
  // came from the chart or a screener row, which charts it. Once per symbol, so
  // picking another tab on the same ETF sticks.
  const etfOpened = useRef<string | null>(null);
  useEffect(() => {
    if (!etf || etfOpened.current === shown) return;
    etfOpened.current = shown;
    onTab("etfComponents");
  }, [etf, shown, onTab]);
  // A view out in its own window is not also shown here (`shownView`); with the
  // phone's cards there are no windows, so every view is home.
  const viewDetach = useViewDetach();
  const out = cards ? [] : (viewDetach?.detached ?? []);
  const home = shownView(views, tab, out);
  const active = home ?? views[0]!;
  const [hoverTab, setHoverTab] = useState<SymbolView["id"] | null>(null);

  // **Which symbol changes are ours.**
  //
  // Re-pointing the chart sends the new symbol straight back down as a prop, and
  // without this the arriving prop would look exactly like the user aiming the
  // chart somewhere else — clearing the trail and stranding the reader one click
  // from a way back they can no longer see.
  const requested = useRef<string | null>(null);

  useEffect(() => {
    if (requested.current && symbol.toUpperCase() === requested.current) return;
    requested.current = null;
    setTrail([]);
  }, [symbol]);

  /** Follow a symbol: the panel moves, and the chart with it. */
  const goTo = (next: string, path: string[]) => {
    const target = next.toUpperCase();
    requested.current = target;
    setTrail(path);
    onSymbol?.(target);
  };

  const onPeer = (next: string) => {
    // Never push the symbol already on screen: a double click on a peer would
    // otherwise cost two presses of Back to undo.
    if (next.toUpperCase() === shown.toUpperCase()) return;
    goTo(next, trail.length > 0 ? [...trail, next.toUpperCase()] : [symbol, next.toUpperCase()]);
  };

  const trailStrip =
    trail.length > 1 ? (
      <div style={TRAIL}>
        <button
          type="button"
          style={TRAIL_BACK}
          onClick={() => goTo(trail[trail.length - 2]!, trail.slice(0, -1))}
          title={`Back to ${trail[trail.length - 2]}`}
        >
          ← {trail[trail.length - 2]}
        </button>
        <span style={TRAIL_NOW}>{shown}</span>
        {trail.length > 2 ? (
          <button
            type="button"
            style={TRAIL_ROOT}
            onClick={() => goTo(trail[0]!, [trail[0]!])}
            title={`Back to ${trail[0]}`}
          >
            {trail[0]}
          </button>
        ) : null}
      </div>
    ) : null;

  if (cards) {
    return (
      <DataCards
        labels={views}
        active={active.id}
        onActive={(id) => onTab(id as SymbolView["id"])}
        header={trailStrip}
        symbol={shown}
        render={(id) => {
          const view = views.find((v) => v.id === id);
          if (!view) return null;
          const Render = cards.phone?.[view.id] ?? view.Render;
          return (
            <PhoneCard.Provider value>
              <Render symbol={shown} chartGroupId={chartGroupId} onSymbol={onPeer} />
            </PhoneCard.Provider>
          );
        }}
      />
    );
  }

  return (
    <div
      // `data-tour`: the panel the tour opens to show where Fin St lives.
      data-tour="data-panel"
      style={{
        width: width ?? "100%",
        // **A height, never the content's.** Detached takes the window's;
        // docked takes a fixed one. Either way the tab body scrolls rather than
        // the panel growing — a child sized to its content inside a dialog with
        // `overflow: hidden` is not scrolled, it is *clipped*, and a long news
        // list simply ended in mid-air at the fold.
        height: width === undefined ? "100%" : DOCKED_HEIGHT,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
      }}
    >
        <div role="tablist" aria-label="Symbol data" style={TABS}>
          {views.map((view) => {
            const isOut = out.includes(view.id);
            return (
              // **A view detaches from its own tab** (owner, 2026-10-28): ⧉ in
              // the tab's corner on hover, drawn over the tab rather than beside
              // it so the strip's widths never move under the pointer. Out, the
              // tab stays — where the reader learned it is — marked ⧉, and a
              // click brings its window forward instead of showing it twice.
              <div
                key={view.id}
                style={TAB_HOST}
                onPointerEnter={() => setHoverTab(view.id)}
                onPointerLeave={() => setHoverTab((t) => (t === view.id ? null : t))}
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isOut && view.id === active.id}
                  onClick={() => (isOut ? viewDetach?.focus(view.id) : onTab(view.id))}
                  title={isOut ? `${view.label} is in its own window — show it` : undefined}
                  style={{
                    ...TAB,
                    ...(!isOut && home && view.id === active.id ? TAB_ON : {}),
                  }}
                >
                  {view.id === "summary" && <QueVeeMark size={17} style={{ marginRight: 4, verticalAlign: "-4px" }} />}
                  {view.label}
                  {isOut && <span aria-hidden="true">⧉</span>}
                  {/* A view with no source is marked rather than hidden, so the
                      strip says what is coming as well as what is here. */}
                  {!view.ready && <span style={DOT} title="No source yet" />}
                </button>
                {viewDetach && !isOut && hoverTab === view.id && (
                  <button
                    type="button"
                    onClick={() => viewDetach.detach(view.id)}
                    title={`Open ${view.label} in its own window`}
                    aria-label={`Open ${view.label} in its own window`}
                    style={TAB_DETACH}
                  >
                    ⧉
                  </button>
                )}
              </div>
            );
          })}
        </div>
      {trailStrip}
      <div role="tabpanel" style={BODY}>
        {home ? (
          <active.Render symbol={shown} chartGroupId={chartGroupId} onSymbol={onPeer} />
        ) : (
          <p style={{ color: "var(--qf-text-muted)" }}>Every view is open in its own window.</p>
        )}
      </div>
    </div>
  );
}

/**
 * One view, alone in its own window — no tab strip, because the window *is*
 * the tab (owner's mock, 2026-10-28).
 *
 * It follows the same chart the panel would (`App` hands it the panel's target),
 * so switching layouts re-points it too. Following a peer re-points that chart,
 * as in the panel; the trail is the panel's and is not kept here.
 */
export function SymbolDataView({
  symbol,
  view: id,
  onSymbol,
  chartGroupId,
}: {
  symbol: string;
  view: SymbolView["id"];
  onSymbol?: ((symbol: string) => void) | undefined;
  chartGroupId?: string | undefined;
}) {
  // Asked of this symbol's views: News has no India source, and a window that
  // stays open on `RELIANCE.NS` should say so rather than render a dead feed.
  const view = symbolViewsFor(symbol).find((v) => v.id === id);
  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div role="tabpanel" style={BODY}>
        {view ? (
          <view.Render
            symbol={symbol}
            {...(chartGroupId ? { chartGroupId } : {})}
            {...(onSymbol ? { onSymbol } : {})}
          />
        ) : (
          <p style={{ color: "var(--qf-text-muted)" }}>Not available for {symbol}.</p>
        )}
      </div>
    </div>
  );
}

/**
 * The trail strip, shown only while wandering.
 *
 * Absent at rest rather than always present and empty: a panel aimed at one
 * symbol has nowhere to go back to, and a permanently disabled control is a
 * control that has to be read before it can be ignored.
 */
const TRAIL: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  padding: "3px 8px",
  borderBottom: "1px solid var(--qf-border)",
  fontSize: 10.5,
};

const TRAIL_BACK: React.CSSProperties = {
  font: "inherit",
  fontSize: 10.5,
  padding: "1px 6px",
  borderRadius: 3,
  border: "1px solid var(--qf-border)",
  background: "transparent",
  color: "var(--qf-text-secondary)",
  cursor: "pointer",
};

const TRAIL_NOW: React.CSSProperties = {
  fontWeight: 600,
  color: "var(--qf-text)",
};

/** Straight home from three levels deep, without pressing Back three times. */
const TRAIL_ROOT: React.CSSProperties = {
  font: "inherit",
  fontSize: 10.5,
  marginLeft: "auto",
  padding: "1px 6px",
  border: 0,
  background: "none",
  color: "var(--qf-accent)",
  cursor: "pointer",
};

const HOVER_HOST: React.CSSProperties = {
  position: "fixed",
  zIndex: 60,
  maxHeight: "70vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  borderRadius: 6,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-surface)",
  boxShadow: "0 8px 32px rgba(0,0,0,0.45)",
};

const DETACH: React.CSSProperties = {
  width: 18,
  height: 18,
  padding: 0,
  border: "none",
  borderRadius: 3,
  background: "transparent",
  color: "var(--qf-accent)",
  font: "inherit",
  fontSize: 12,
  lineHeight: 1,
  cursor: "pointer",
};

/**
 * The same control, once the browser has refused.
 *
 * Kept in place and dimmed rather than removed: a control that disappears
 * between one click and the next reads as the click having broken something.
 * `not-allowed` is the only cursor that says "this cannot work" before it is
 * pressed, which is the whole point of leaving it on screen.
 */
const DETACH_REFUSED: React.CSSProperties = {
  ...DETACH,
  color: "var(--qf-text-muted)",
  cursor: "not-allowed",
};

/**
 * The slot host: the cell's whole box, and no frame of its own.
 *
 * No border and no radius — `SectionGrid` draws both on the cell this sits in,
 * and a second rounded edge 1px inside the first is the notch the grid's own
 * radius comment is about. `100%` on both axes rather than a size, because the
 * track was sized for the screener and is what the reader chose.
 */
const SLOT_HOST: React.CSSProperties = {
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  minHeight: 0,
  overflow: "hidden",
  background: "var(--qf-surface)",
  color: "var(--qf-text)",
};

/**
 * The header row, matched to the screener's group bar rather than to the dialog.
 *
 * This panel stands where that bar stood a click ago, and a header of a
 * different height there makes the tabs under it jump when the two swap.
 */
const SLOT_HEAD: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "0.3rem 0.5rem",
  borderBottom: "1px solid var(--qf-border)",
  background: "var(--qf-surface-alt)",
  flex: "0 0 auto",
};

const SLOT_TITLE: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

/** The ✕, pushed to the end — the same place the screener's own hide sits. */
const SLOT_CLOSE: React.CSSProperties = {
  marginLeft: "auto",
  width: 20,
  height: 20,
  padding: 0,
  border: "none",
  borderRadius: 3,
  background: "transparent",
  color: "var(--qf-text-secondary)",
  font: "inherit",
  fontSize: 12,
  lineHeight: 1,
  cursor: "pointer",
  flex: "0 0 auto",
};

const TABS: React.CSSProperties = {
  display: "flex",
  gap: 1,
  padding: "0 5px",
  background: "var(--qf-surface-alt)",
  borderBottom: "1px solid var(--qf-border)",
  overflowX: "auto",
  flex: "0 0 auto",
};

const TAB: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 4,
  padding: "6px 8px",
  border: "none",
  borderBottom: "2px solid transparent",
  background: "transparent",
  color: "var(--qf-text-muted)",
  font: "inherit",
  fontSize: 11.5,
  whiteSpace: "nowrap",
  cursor: "pointer",
};

const TAB_ON: React.CSSProperties = {
  color: "var(--qf-accent)",
  borderBottomColor: "var(--qf-accent)",
};

/** A tab and its hover ⧉, which sits over the tab's corner and takes no width. */
const TAB_HOST: React.CSSProperties = {
  position: "relative",
  flex: "0 0 auto",
  display: "flex",
};

const TAB_DETACH: React.CSSProperties = {
  position: "absolute",
  top: 1,
  right: 0,
  width: 14,
  height: 14,
  padding: 0,
  border: "none",
  borderRadius: 3,
  background: "var(--qf-surface)",
  color: "var(--qf-accent)",
  font: "inherit",
  fontSize: 10,
  lineHeight: "14px",
  cursor: "pointer",
};

const DOT: React.CSSProperties = {
  display: "inline-block",
  width: 5,
  height: 5,
  borderRadius: 99,
  background: "var(--qf-text-muted)",
};

const BODY: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  padding: 11,
  display: "flex",
  flexDirection: "column",
  gap: 10,
};
